import type {
  AnalyzeTradeFlow,
  AnalyzeTradeFlowTotals,
  AnalyzeTradeFlowVenue,
  AnalyzeTradeOpenInterestVenue,
  AnalyzeTradeOpenInterestWindow,
} from '@/types'

export interface TradeValues {
  buy: number
  sell: number
  total: number
  net: number
  /** Percentage of notional; null when there is no measured volume. */
  buyShare: number | null
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function positive(value: unknown): number {
  return Math.max(0, finite(value) ?? 0)
}

/** Recompute the display from sides so stale summary fields cannot disagree. */
export function tradeValues(values?: Partial<AnalyzeTradeFlowTotals> | null): TradeValues {
  let buy: number
  let sell: number
  if (values && ('buy_notional' in values || 'sell_notional' in values)) {
    buy = positive(values.buy_notional)
    sell = positive(values.sell_notional)
  } else {
    const total = positive(values?.total_notional)
    const net = Math.max(-total, Math.min(total, finite(values?.net_notional) ?? 0))
    buy = total / 2 + net / 2
    sell = total / 2 - net / 2
  }
  const total = buy + sell
  if (!Number.isFinite(total)) return { buy: 0, sell: 0, total: 0, net: 0, buyShare: null }
  return { buy, sell, total, net: buy - sell, buyShare: total > 0 ? buy / total * 100 : null }
}

function quote(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim().toUpperCase() : 'UNKNOWN'
}

/** USD and USDT share display units at 1:1; stored quote amounts stay unchanged. */
export function tradeQuoteCurrency(value: unknown): string {
  const currency = quote(value)
  return currency === 'USD' || currency === 'USDT' ? 'USD / USDT' : currency
}

/** Combine USD/USDT while keeping other currencies and unknown mixed units separate. */
export function selectTradeCurrency(flow: AnalyzeTradeFlow, requested?: string) {
  const venues = Array.isArray(flow.venues) ? flow.venues : []
  const groups = new Map<string, Partial<AnalyzeTradeFlowTotals>>()
  const entries = Object.entries(flow.notional_by_currency ?? {})
  let mixed = venues.some((venue) => quote(venue.quote_currency) === 'MIXED')
  for (const [rawCurrency, rawValues] of entries) {
    const currency = tradeQuoteCurrency(rawCurrency)
    if (currency === 'MIXED') { mixed = true; continue }
    if (!rawValues || typeof rawValues !== 'object') continue
    const previous = tradeValues(groups.get(currency))
    const next = tradeValues(rawValues)
    groups.set(currency, {
      buy_notional: previous.buy + next.buy,
      sell_notional: previous.sell + next.sell,
    })
  }
  if (!groups.size) {
    // Older API responses lack the map; recover only explicitly scoped venues.
    for (const venue of venues) {
      const currency = tradeQuoteCurrency(venue.quote_currency)
      if (currency === 'MIXED' || currency === 'UNKNOWN') continue
      const previous = tradeValues(groups.get(currency))
      const next = tradeValues(venue)
      groups.set(currency, {
        buy_notional: previous.buy + next.buy,
        sell_notional: previous.sell + next.sell,
      })
    }
    if (!groups.size && !mixed) groups.set('UNKNOWN', flow.totals ?? {})
  }
  const currencies = [...groups.keys()].sort((a, b) => {
    const priority = (currency: string) => currency === 'USD / USDT' ? 0 : currency === 'USDC' ? 1 : 2
    return priority(a) - priority(b) || a.localeCompare(b)
  })
  mixed ||= currencies.length > 1
  const preferred = tradeQuoteCurrency(requested)
  const currency = requested && groups.has(preferred) ? preferred : currencies[0] ?? 'UNKNOWN'
  return {
    currency,
    currencies,
    mixed,
    values: tradeValues(groups.get(currency)),
    venues: venues.filter((venue) => tradeQuoteCurrency(venue.quote_currency) === currency && currency !== 'MIXED') as AnalyzeTradeFlowVenue[],
  }
}

/** OI is net outstanding coin quantity, never a count of opening trades. */
export function oiValues(oi?: Partial<AnalyzeTradeOpenInterestVenue | AnalyzeTradeOpenInterestWindow> | null) {
  if (!oi || !['ready', 'partial'].includes(oi.status ?? '')) return null
  if ('matched_venues' in oi && positive(oi.matched_venues) === 0) return null
  const start = finite(oi.start_coins)
  const end = finite(oi.end_coins)
  if (start === null || end === null || start < 0 || end < 0) return null
  const change = end - start
  const percent = start > 0 ? change / start * 100 : null
  return { start, end, change, percent: percent !== null && Number.isFinite(percent) ? percent : null }
}

function trimDecimals(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value
}

export function formatTradeAmount(value: number, signed = false): string {
  const number = finite(value)
  if (number === null) return '—'
  const absolute = Math.abs(number)
  let magnitude = ''
  for (const [divisor, suffix] of [[1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']] as const) {
    if (absolute >= divisor) {
      const scaled = absolute / divisor
      magnitude = (scaled >= 10000 ? scaled.toExponential(1) : trimDecimals(scaled.toFixed(2))) + suffix
      break
    }
  }
  if (!magnitude) {
    magnitude = trimDecimals(absolute.toFixed(absolute < 1 ? 6 : 2))
    if (absolute > 0 && magnitude === '0') magnitude = absolute.toPrecision(2)
  }
  return (number < 0 ? '−' : signed && number > 0 ? '+' : '') + magnitude
}

export function formatOiPercent(value: number | null): string {
  const number = finite(value)
  if (number === null) return '—'
  const absolute = Math.abs(number)
  const magnitude = absolute >= 1000 ? formatTradeAmount(absolute)
    : absolute > 0 && absolute < 0.1 ? String(Number(absolute.toPrecision(2))) : absolute.toFixed(1)
  return (number < 0 ? '−' : number > 0 ? '+' : '') + magnitude + '%'
}

export function tradeWarnings(flow: AnalyzeTradeFlow): string[] {
  const warnings: string[] = []
  const expected = positive(flow.coverage?.expected_buckets)
  const observed = positive(flow.coverage?.observed_buckets)
  if (flow.status === 'disabled') warnings.push('Collection disabled')
  if (flow.status === 'no_data' || (expected > 0 && observed === 0)) warnings.push('No trade data')
  else if (expected > 0 && observed < expected) {
    const unit = flow.bucket_seconds === 60 ? 'minutes' : 'buckets'
    warnings.push(`Partial data · ${observed}/${expected} ${unit}`)
  }
  if (flow.data_quality?.is_stale || flow.data_quality?.warnings?.includes('stale_data')) warnings.push('Stale data')
  if (positive(flow.trade_stream_state?.summary?.problem_count)) warnings.push('Some feeds need attention')
  if (!warnings.length && flow.status === 'degraded') warnings.push('Data quality needs attention')
  if (!warnings.length && flow.status === 'legacy') warnings.push('Recurring-trade detection unavailable')
  return warnings
}

const oiReasons: Record<string, string> = {
  no_futures_venues: 'No futures exchanges in this trade window',
  no_comparable_endpoints: 'No comparable readings for this window',
  no_comparable_readings: 'No comparable readings for this window',
  missing_observation: 'No reading near the window boundary',
  stale_observation: 'Reading is too old for the window boundary',
  future_observation: 'Reading falls after the window boundary',
  invalid_observation: 'Stored reading is invalid',
  query_unavailable: 'Stored readings could not be loaded',
  unverified_coin_units: 'Coin units could not be verified',
  resolution_too_coarse: 'Stored averages are too coarse for this window',
  overlapping_endpoints: 'Start and end readings overlap',
  invalid_window: 'Invalid comparison window',
  invalid_total: 'OI total could not be calculated',
}

export function oiReason(reason?: string | null): string {
  if (!reason) return 'No comparable readings for this window'
  for (const endpoint of ['start', 'end']) {
    const prefix = `${endpoint}_`
    if (reason.startsWith(prefix) && oiReasons[reason.slice(prefix.length)]) {
      return `${endpoint === 'start' ? 'Start' : 'End'}: ${oiReasons[reason.slice(prefix.length)]}`
    }
  }
  return oiReasons[reason] ?? 'No comparable readings for this window'
}
